import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { AIAgentType, BaseAIAgentExecutionResult, BaseAIAgentInteractionResult } from '../../models/AIAgent'
import { createSSEParser } from '../../common/streamingUtils'
import { StreamingEvent } from '../../models/StreamingAIAgent'

const _BASE_URL_EXECUTE = import.meta.env.REACT_APP_AI_EXECUTE_API_BASE_URL!

export interface AIAgentAttachment {
  fileId: string
}

export interface AIAgentExecutionPayload {
  type: AIAgentType
  context: { [key: string]: any }
  attachments?: AIAgentAttachment[]
}

export interface ExecuteAIAgentRequest {
  accountId: string
  payload: AIAgentExecutionPayload
}

export interface InteractWithAIAgentRequest {
  accountId: string
  aiAgentId: string
  payload: { [key: string]: any }
}

export interface InteractWithAIAgentStreamingRequest extends InteractWithAIAgentRequest {
  onStreamingEvent: (event: StreamingEvent) => Promise<void>
}

export const aiAgentApi = createApi({
  reducerPath: 'aiAgentApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL_EXECUTE),
  endpoints: (builder) => ({
    executeAiAgent: builder.mutation<BaseAIAgentExecutionResult, ExecuteAIAgentRequest>({
      query: ({ accountId, payload }) => ({
        url: `/accounts/${accountId}/ai-agent/execute`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: { data: BaseAIAgentExecutionResult }) => response.data,
    }),
    interactWithAiAgent: builder.mutation<BaseAIAgentInteractionResult, InteractWithAIAgentRequest>({
      query: ({ accountId, aiAgentId, payload }) => ({
        url: `/accounts/${accountId}/ai-agent/${aiAgentId}/interact`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: { data: BaseAIAgentInteractionResult }) => response.data,
    }),
    interactWithAiAgentStreaming: builder.mutation<undefined, InteractWithAIAgentStreamingRequest>({
      query: ({ accountId, aiAgentId, payload, onStreamingEvent }) => ({
        url: `/accounts/${accountId}/ai-agent/${aiAgentId}/interact/stream`,
        method: 'POST',
        body: payload,
        responseHandler: async (response) => {
          const { body } = response
          if (!body) {
            return
          }
          const reader = body.getReader()
          const textDecoder = new TextDecoder()
          const sseParser = createSSEParser(onStreamingEvent)

          while (true) {
            const { value, done } = await reader.read()
            if (done) {
              break
            }
            const decoded = textDecoder.decode(value, { stream: true })
            sseParser.feed(decoded)
          }
        },
      }),
    }),
  }),
})

export const { useExecuteAiAgentMutation, useInteractWithAiAgentMutation, useInteractWithAiAgentStreamingMutation } = aiAgentApi
